import { AppNotification } from 'components/views/company/outreach-template/proposals/types';
import { useAuthContext } from 'providers/auth';
import { useOutreachActivity } from 'providers/outreach-activity';
import { toAppNotification } from 'providers/outreach-activity/map-activity-notification';
import { addReadIds, useReadIds } from 'providers/outreach-activity/notification-reads';
import { useCallback, useMemo } from 'react';

export interface UseNotificationsResult {
  items: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  /** Mark the current view's notifications read; pass a refType to limit to that source. */
  markAllRead: (refType?: string) => void;
}

export const useNotifications = (): UseNotificationsResult => {
  const { isInternal, user } = useAuthContext();
  const role = isInternal ? 'admin' : 'client';
  const userSub = user.sub ?? '';
  const { events } = useOutreachActivity();

  const readIds = useReadIds();

  // No sort: the provider already emits `events` newest-first, and filter/map preserve order.
  const items = useMemo(
    () =>
      events
        .filter((e) => e.recipientRole === role)
        .map((e) => toAppNotification(e, readIds, isInternal, userSub)),
    [events, role, readIds, isInternal, userSub],
  );

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const markRead = useCallback((id: string) => addReadIds([id]), []);

  // Unread-only: addReadIds only short-circuits on an EMPTY list, so including already-read ids
  // would write localStorage and fire a cross-tab event on every click even with nothing unread —
  // and re-stamp those entries' `at`, pushing their prune horizon out indefinitely.
  const markAllRead = useCallback(
    (refType?: string) => {
      addReadIds(
        items.filter((n) => !n.read && (!refType || n.refType === refType)).map((n) => n.id),
      );
    },
    [items],
  );

  return { items, unreadCount, markRead, markAllRead };
};
